import { AdElementCasinoAsset } from '@adflow/types';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Flex,
  Heading,
  IconButton,
  Select,
} from '@chakra-ui/react';
import { ChangeEventHandler, FC, useState } from 'react';
import { useStore } from '../../../hooks/useStore';
import { LuArrowLeft, LuArrowRight } from 'react-icons/lu';
import { CASINO_IMAGES_BASE_URL, randomImagesIds } from '../../../constants/constants';

type CasinoAssetControlsProps = {
  selectedElement: AdElementCasinoAsset;
};

const validValues = ['SQUARE_IMAGE', 'LANDSCAPE_IMAGE', 'PORTRAIT_IMAGE'] as const;

const CasinoAssetControls: FC<CasinoAssetControlsProps> = ({ selectedElement }) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const options = [
    { value: 'SQUARE_IMAGE', label: 'Square Image' },
    { value: 'LANDSCAPE_IMAGE', label: 'Landscape Image' },
    { value: 'PORTRAIT_IMAGE', label: 'Portrait Image' },
  ];

  const { updateElementData } = useStore((state) => ({
    updateElementData: state.updateElementData,
  }));

  const handleSourceChange: ChangeEventHandler<HTMLSelectElement> = (event) => {
    const value = event.target.value as (typeof validValues)[number];

    toggleImage('none', value);
  };

  const isProperlyConfigured = (): boolean => {
    const isDefaultSource = validValues.includes(
      selectedElement.name as (typeof validValues)[number],
    );

    return selectedElement.data.source === selectedElement.name || !isDefaultSource;
  };

  const toggleImage = (
    direction: 'left' | 'right' | 'none',
    newValue?: (typeof validValues)[number],
  ) => {
    let newIndex;

    if (direction === 'left' && selectedIndex === 0) {
      newIndex = randomImagesIds.length - 1;
    } else if (direction === 'right' && selectedIndex === randomImagesIds.length - 1) {
      newIndex = 0;
    } else {
      newIndex =
        direction === 'left'
          ? selectedIndex - 1
          : direction === 'right'
          ? selectedIndex + 1
          : selectedIndex;
    }

    const data = newValue || selectedElement.data.source;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const selectedOption = data!.split('_')[0].toLowerCase();

    const content = `${CASINO_IMAGES_BASE_URL}/${selectedOption}/${randomImagesIds[newIndex]}`;

    updateElementData(selectedElement.id, {
      ...selectedElement.data,
      content,
      source: newValue || selectedElement.data.source,
    });

    setSelectedIndex(newIndex);
  };

  return (
    <Flex direction="column">
      <Heading fontSize="lg" fontWeight="semibold" mb="2">
        Source
      </Heading>
      <Select
        placeholder="Select option"
        value={selectedElement.data.source ?? ''}
        onChange={handleSourceChange}
      >
        {options.map((option) => {
          return (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          );
        })}
      </Select>
      <Flex alignItems="center" justifyContent="space-between" mt="6" gap="4">
        <IconButton onClick={() => toggleImage('left')} aria-label="Left">
          <LuArrowLeft />
        </IconButton>
        <div>Cycle game images</div>
        <IconButton onClick={() => toggleImage('right')} aria-label="Left">
          <LuArrowRight />
        </IconButton>
      </Flex>
      {!isProperlyConfigured() && (
        <Flex mt="6" alignItems="center" justifyContent="center">
          <Alert status="warning" borderRadius="md" w="100%">
            <AlertIcon />
            <AlertDescription>
              The selected source differs from the resource name. This is still valid, but keeping
              names and content aligned helps avoid confusion later in the workflow.
            </AlertDescription>
          </Alert>
        </Flex>
      )}
    </Flex>
  );
};

CasinoAssetControls.displayName = 'CasinoAssetControls';
export default CasinoAssetControls;
